import { useState, useEffect, useCallback, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import api from '../../services/api'
import toast from 'react-hot-toast'
import {
  FolderOpen,
  File,
  Upload,
  Download,
  Trash2,
  FolderPlus,
  ArrowLeft,
  HardDrive,
  AlertCircle,
  Loader2,
  RefreshCw,
  Monitor,
  WifiOff,
  ChevronRight,
  Clock,
} from 'lucide-react'
import LoadingSpinner from '../../components/LoadingSpinner'
import {
  AGENT_URL,
  fetchDeviceCode,
  getExt,
  fileTypeLabel,
  useWorkspaceFileActions,
  useExtensionAppMap,
} from '../../hooks/useWorkspaceFileActions'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatBytes(bytes) {
  if (bytes === null || bytes === undefined) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

// Relative, human-readable modified date: Today / Yesterday / Sep 10
function formatDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  const now = new Date()
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const startOfYesterday = new Date(startOfToday.getTime() - 86400000)
  if (d >= startOfToday) return 'Today'
  if (d >= startOfYesterday) return 'Yesterday'
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

const DEFAULT_FOLDERS = ['My Work', 'Projects', 'Exports', 'Downloads']

// At the My Files root, show only ONE student-facing concept: Projects.
// "My Work", "Downloads", and "Exports" still exist on disk (the Agent creates
// them), but exposing multiple top-level folders forces the student to think
// "which of these am I supposed to use?" A student thinks in terms of projects,
// not in terms of filesystem categories. Recent Work (above) already surfaces
// recently modified files from anywhere in the workspace, so files previously
// saved into "My Work" remain discoverable and openable even though the folder
// is no longer shown at the root.
const ROOT_VISIBLE_FOLDERS = ['Projects']

// Descriptive subtitle for the single primary folder at the root. Inside a
// folder (any depth), the generic "Updated {date}" subtitle is used instead.
const ROOT_FOLDER_DESCRIPTIONS = {
  'Projects': 'Your creative projects and working files',
}

// Persist the current folder across browser refreshes. sessionStorage is
// scoped to this tab and cleared when the tab closes — appropriate for a
// session-scoped workspace. Without this, a hard refresh resets currentPath
// to '' and the student thinks their files "disappeared" (they're still on
// disk; the user was just navigated back to the workspace root).
const PATH_STORAGE_KEY = 'digital-lab-workspace-path'

function loadStoredPath() {
  try {
    const stored = sessionStorage.getItem(PATH_STORAGE_KEY)
    return typeof stored === 'string' ? stored : ''
  } catch {
    return ''
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function DigitalLabWorkspace() {
  const queryClient = useQueryClient()
  const [deviceCode, setDeviceCode] = useState(null)
  const [agentReachable, setAgentReachable] = useState(true)
  const [agentRetrying, setAgentRetrying] = useState(false)
  const [currentPath, setCurrentPathState] = useState(loadStoredPath)
  const [showCreateFolder, setShowCreateFolder] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [importing, setImporting] = useState(false)
  const importInputRef = useRef(null)

  // Wrap setCurrentPath so the folder is persisted across refreshes.
  const setCurrentPath = useCallback((updater) => {
    setCurrentPathState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      try {
        if (next) sessionStorage.setItem(PATH_STORAGE_KEY, next)
        else sessionStorage.removeItem(PATH_STORAGE_KEY)
      } catch {
        /* sessionStorage may be unavailable (private mode) — non-fatal */
      }
      return next
    })
  }, [])

  // Discover which workstation we're on via the local Agent.
  // Retry for up to 30 seconds before declaring the Agent unreachable.
  useEffect(() => {
    let mounted = true
    let retryCount = 0
    const MAX_RETRIES = 15 // 15 × 2s = 30s total
    const RETRY_DELAY = 2000

    function attemptFetch() {
      fetchDeviceCode()
        .then((code) => {
          if (mounted) {
            setDeviceCode(code)
            setAgentReachable(true)
            setAgentRetrying(false)
          }
        })
        .catch(() => {
          if (!mounted) return
          retryCount++
          if (retryCount < MAX_RETRIES) {
            setAgentRetrying(true)
            setTimeout(attemptFetch, RETRY_DELAY)
          } else {
            setAgentReachable(false)
            setAgentRetrying(false)
          }
        })
    }

    attemptFetch()
    return () => { mounted = false }
  }, [])

  // Get workspace metadata from backend
  const { data: workspaceMeta, isLoading: metaLoading } = useQuery({
    queryKey: ['digital-lab-workspace-meta', deviceCode],
    queryFn: async () => {
      const { data } = await api.get('/digital-lab/student/workspace', {
        params: deviceCode ? { device_code: deviceCode } : {},
      })
      return data
    },
    enabled: !!deviceCode,
    refetchInterval: 60 * 1000,
  })

  // Get workspace token from backend.
  // The backend JWT expires in 5 minutes. We refresh every 3 minutes to keep
  // a comfortable safety margin, and allow React Query's default retry on
  // transient failures so a single network blip doesn't strand the token.
  // (Previously: retry:false + 4min interval meant one failed refetch left
  // the token stale until the next interval tick, producing 403s.)
  const {
    data: tokenData,
    refetch: refetchToken,
  } = useQuery({
    queryKey: ['digital-lab-workspace-token', deviceCode],
    queryFn: async () => {
      const { data } = await api.post('/digital-lab/student/workspace/token', {
        device_code: deviceCode,
      })
      return data
    },
    enabled: !!deviceCode,
    refetchInterval: 3 * 60 * 1000, // refresh well before 5min expiry
    retry: 2,
  })

  const workspaceToken = tokenData?.workspace_token

  // Approved applications on this workstation — used to map file extensions to
  // the correct native application for click-to-open.
  const { data: appsData } = useQuery({
    queryKey: ['digital-lab-workspace-apps', deviceCode],
    queryFn: async () => {
      const { data } = await api.get('/digital-lab/student/applications')
      return data
    },
    enabled: !!deviceCode,
    staleTime: 5 * 60 * 1000,
  })

  const extAppMap = useExtensionAppMap(appsData)

  // Shared workspace file actions — one implementation, used by both My Files
  // and the project page. See hooks/useWorkspaceFileActions.js.
  const {
    withWorkspaceTokenRetry,
    openFile: openWorkspaceFile,
    downloadFile: handleDownload,
    opening,
  } = useWorkspaceFileActions({ workspaceToken, refetchToken, deviceCode, extAppMap })

  // Local wrapper preserving this page's call signature: Recent Work passes an
  // explicit folder (a file may live in any subfolder), everything else opens
  // relative to the folder currently being browsed.
  const openFile = useCallback(
    (entry, dirPath) => openWorkspaceFile(entry, dirPath !== undefined ? dirPath : currentPath),
    [openWorkspaceFile, currentPath]
  )

  // List workspace contents from Agent
  const {
    data: listingData,
    isLoading: listingLoading,
    refetch: refetchListing,
    error: listingError,
  } = useQuery({
    queryKey: ['digital-lab-workspace-listing', workspaceToken, currentPath],
    queryFn: async () => {
      const res = await withWorkspaceTokenRetry((token) => {
        const params = new URLSearchParams({ token })
        if (currentPath) params.set('path', currentPath)
        return fetch(`${AGENT_URL}/workspace?${params}`)
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to list workspace')
      return data
    },
    enabled: !!workspaceToken,
    refetchOnWindowFocus: true,
    refetchInterval: 15 * 1000, // reflect filesystem changes without manual refresh
    retry: 1, // the wrapper already retries once on token expiry; don't hammer
  })

  // Recent Work — the student's most recently modified files across their
  // entire workspace, regardless of subfolder. This is the primary view: the
  // student returns to Digital Lab and immediately sees what they were working
  // on, without navigating folders. Backed by Agent GET /workspace/recent
  // (recursive mtime walk, token-validated, student-scoped).
  const {
    data: recentData,
    isLoading: recentLoading,
  } = useQuery({
    queryKey: ['digital-lab-workspace-recent', workspaceToken],
    queryFn: async () => {
      const res = await withWorkspaceTokenRetry((token) =>
        fetch(`${AGENT_URL}/workspace/recent?token=${token}&limit=10`)
      )
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to load recent work')
      return data
    },
    enabled: !!workspaceToken,
    refetchOnWindowFocus: true,
    refetchInterval: 30 * 1000, // recent work changes less often than a single folder
    retry: 1,
  })

  const recentFiles = recentData?.entries || []

  // List SMA Resources from Agent
  const { data: resourcesData, isLoading: resourcesLoading } = useQuery({
    queryKey: ['digital-lab-workspace-resources', workspaceToken],
    queryFn: async () => {
      const res = await withWorkspaceTokenRetry((token) =>
        fetch(`${AGENT_URL}/workspace/resources?token=${token}`)
      )
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to list resources')
      return data
    },
    enabled: !!workspaceToken,
  })

  // Create folder mutation
  const createFolderMutation = useMutation({
    mutationFn: async (folderPath) => {
      const res = await withWorkspaceTokenRetry((token) =>
        fetch(`${AGENT_URL}/workspace/folders`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ workspace_token: token, path: folderPath }),
        })
      )
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to create folder')
      return data
    },
    onSuccess: () => {
      toast.success('Folder created')
      setShowCreateFolder(false)
      setNewFolderName('')
      queryClient.invalidateQueries({ queryKey: ['digital-lab-workspace-listing'] })
    },
    onError: (err) => toast.error(err.message),
  })

  // Upload (import) file mutation — base64 content copied into the workspace.
  const uploadFileMutation = useMutation({
    mutationFn: async ({ file, targetPath }) => {
      const content_base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          const result = reader.result
          resolve(result.substring(result.indexOf(',') + 1))
        }
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
      const res = await withWorkspaceTokenRetry((token) =>
        fetch(`${AGENT_URL}/workspace/files`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workspace_token: token,
            path: targetPath,
            content_base64,
          }),
        })
      )
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to import file')
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['digital-lab-workspace-listing'] })
    },
    onError: (err) => toast.error(err.message),
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (itemPath) => {
      const res = await withWorkspaceTokenRetry((token) =>
        fetch(
          `${AGENT_URL}/workspace/files?token=${token}&path=${encodeURIComponent(itemPath)}`,
          { method: 'DELETE' }
        )
      )
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to delete')
      return data
    },
    onSuccess: () => {
      toast.success('Deleted')
      queryClient.invalidateQueries({ queryKey: ['digital-lab-workspace-listing'] })
    },
    onError: (err) => toast.error(err.message),
  })

  // Download file
  // Import to Projects — the browser's native file picker IS the Windows file
  // picker. Selected files are copied into the current folder (or "Projects"
  // when at the workspace root) so they appear in My Files.
  const handleImportChange = async (e) => {
    const files = Array.from(e.target.files || [])
    e.target.value = ''
    if (!files.length) return
    const dest = currentPath || 'Projects'
    setImporting(true)
    let succeeded = 0
    for (const file of files) {
      const targetPath = `${dest}/${file.name}`
      try {
        await uploadFileMutation.mutateAsync({ file, targetPath })
        succeeded++
      } catch (err) {
        toast.error(`Could not import ${file.name}`)
      }
    }
    setImporting(false)
    if (succeeded > 0) {
      toast.success(
        `Imported ${succeeded} file${succeeded > 1 ? 's' : ''} to ${dest}`
      )
    }
  }

  const navigateInto = (folderName) => {
    setCurrentPath((prev) => (prev ? `${prev}/${folderName}` : folderName))
  }

  const navigateUp = () => {
    setCurrentPath((prev) => {
      if (!prev) return prev
      const parts = prev.split('/')
      parts.pop()
      return parts.join('/')
    })
  }

  const handleCreateFolder = () => {
    if (!newFolderName.trim()) return
    const folderPath = currentPath ? `${currentPath}/${newFolderName.trim()}` : newFolderName.trim()
    createFolderMutation.mutate(folderPath)
  }

  // ─── Render: Agent still initializing ───────────────────────────────────────
  if (agentRetrying) {
    return (
      <div className="w-full max-w-2xl mx-auto min-h-[70vh] flex flex-col justify-center">
        <div className="cinema-page-header">
          <div>
            <span className="cinema-label block mb-2">SMA Digital Lab</span>
            <h1
              className="text-2xl sm:text-3xl text-white"
              style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800 }}
            >
              My Files
            </h1>
          </div>
        </div>
        <div className="cinema-empty">
          <Loader2 size={40} className="mx-auto mb-4 text-cinema-gold/60 animate-spin" />
          <h2
            className="text-lg sm:text-xl text-cinema-soft mb-2"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700 }}
          >
            Loading your SMA environment&hellip;
          </h2>
          <p className="font-sans text-sm text-cinema-muted max-w-md mx-auto">
            Please wait while your workstation connects.
          </p>
        </div>
      </div>
    )
  }

  // ─── Render: Agent not reachable ────────────────────────────────────────────
  if (!agentReachable) {
    return (
      <div className="w-full max-w-2xl mx-auto min-h-[70vh] flex flex-col justify-center">
        <div className="cinema-page-header">
          <div>
            <span className="cinema-label block mb-2">SMA Digital Lab</span>
            <h1
              className="text-2xl sm:text-3xl text-white"
              style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800 }}
            >
              My Files
            </h1>
          </div>
        </div>
        <div className="cinema-empty">
          <AlertCircle size={40} className="mx-auto mb-4 text-cinema-gold/60" />
          <h2
            className="text-lg sm:text-xl text-cinema-soft mb-2"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700 }}
          >
            SMA Computer Agent not detected on this workstation.
          </h2>
          <p className="font-sans text-sm text-cinema-muted max-w-md mx-auto mb-6">
            The workspace requires the SMA Computer Agent to be running. Please
            ensure you are using a registered Digital Lab workstation.
          </p>
          <Link
            to="/student/digital-lab"
            className="cinema-btn-ghost text-xs sm:text-sm px-6 py-3 min-h-[44px]"
          >
            <ArrowLeft size={16} /> Back to Digital Lab
          </Link>
        </div>
      </div>
    )
  }

  // ─── Render: No active session ─────────────────────────────────────────────
  if (workspaceMeta && !workspaceMeta.has_active_session) {
    return (
      <div className="w-full max-w-2xl mx-auto min-h-[70vh] flex flex-col justify-center">
        <div className="cinema-page-header">
          <div>
            <span className="cinema-label block mb-2">SMA Digital Lab</span>
            <h1
              className="text-2xl sm:text-3xl text-white"
              style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800 }}
            >
              My Files
            </h1>
          </div>
        </div>
        <div className="cinema-empty">
          <Monitor size={40} className="mx-auto mb-4 text-cinema-gold/60" />
          <h2
            className="text-lg sm:text-xl text-cinema-soft mb-2"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700 }}
          >
            No active Digital Lab session.
          </h2>
          <p className="font-sans text-sm text-cinema-muted max-w-md mx-auto mb-6">
            Start a Digital Lab session to access your workspace.
          </p>
          <Link
            to="/student/digital-lab"
            className="cinema-btn-ghost text-xs sm:text-sm px-6 py-3 min-h-[44px]"
          >
            <ArrowLeft size={16} /> Go to Digital Lab
          </Link>
        </div>
      </div>
    )
  }

  if (!deviceCode || metaLoading || !workspaceToken) return <LoadingSpinner label="Loading your workspace" />

  const entries = listingData?.entries || []
  const resources = resourcesData?.entries || []
  const disk = listingData?.disk
  const pathParts = currentPath ? currentPath.split('/').filter(Boolean) : []

  // Folders first, then files — both alphabetical from the Agent.
  const folders = entries.filter((e) => e.type === 'folder')
  const files = entries.filter((e) => e.type === 'file')
  const inProjects = currentPath === 'Projects' || currentPath.startsWith('Projects/')

  return (
    <div className="w-full max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="cinema-page-header">
        <div>
          <span className="cinema-label block mb-2">Student · Digital Lab</span>
          <h1
            className="text-2xl sm:text-3xl xl:text-4xl text-white"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800 }}
          >
            My Files
          </h1>
          <p
            className="font-sans text-sm text-cinema-muted mt-2 max-w-xl"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          >
            Your SMA workspace on this computer. Work saved here stays available
            when you return to Digital Lab.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <Link
            to="/student/digital-lab"
            className="cinema-btn-ghost text-xs sm:text-sm px-5 py-3 min-h-[44px]"
          >
            <ArrowLeft size={16} /> Digital Lab
          </Link>
          <button
            onClick={() => importInputRef.current?.click()}
            disabled={importing}
            className="cinema-btn-primary text-xs sm:text-sm px-5 py-3 min-h-[44px] disabled:opacity-50 disabled:cursor-wait"
          >
            {importing ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Upload size={16} />
            )}
            Import to Projects
          </button>
          <input
            ref={importInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleImportChange}
          />
        </div>
      </div>

      {/* Breadcrumb + secondary actions */}
      <div className="flex items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-2 text-sm flex-1 min-w-0 overflow-x-auto">
          <button
            onClick={() => setCurrentPath('')}
            className="font-sans text-cinema-soft hover:text-cinema-gold transition-colors font-medium whitespace-nowrap"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          >
            My Files
          </button>
          {pathParts.map((part, idx) => (
            <span key={idx} className="flex items-center gap-2 whitespace-nowrap">
              <span className="text-cinema-muted/40">/</span>
              <button
                onClick={() => setCurrentPath(pathParts.slice(0, idx + 1).join('/'))}
                className={`font-sans transition-colors ${
                  idx === pathParts.length - 1
                    ? 'text-white font-medium'
                    : 'text-cinema-soft hover:text-cinema-gold'
                }`}
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
              >
                {part}
              </button>
            </span>
          ))}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {currentPath && (
            <button
              onClick={navigateUp}
              className="p-2 text-cinema-muted hover:text-cinema-gold transition-colors"
              title="Go up"
            >
              <ArrowLeft size={16} />
            </button>
          )}
          <button
            onClick={() => refetchListing()}
            className="p-2 text-cinema-muted hover:text-cinema-gold transition-colors"
            title="Refresh"
          >
            <RefreshCw size={16} />
          </button>
          <button
            onClick={() => setShowCreateFolder((v) => !v)}
            className="p-2 text-cinema-muted hover:text-cinema-gold transition-colors"
            title="New folder"
          >
            <FolderPlus size={16} />
          </button>
        </div>
      </div>

      {/* Create folder inline */}
      {showCreateFolder && (
        <div className="flex items-center gap-3 mb-8 pb-6 border-b border-white/5">
          <FolderPlus size={16} className="text-cinema-muted" />
          <input
            type="text"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateFolder()
            }}
            placeholder="Folder name"
            autoFocus
            className="flex-1 max-w-sm bg-transparent border-b border-white/15 focus:border-cinema-gold outline-none py-2 text-sm text-white"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          />
          <button
            onClick={handleCreateFolder}
            disabled={createFolderMutation.isPending || !newFolderName.trim()}
            className="cinema-btn-primary text-xs px-4 py-2 min-h-[36px] disabled:opacity-50"
          >
            Create
          </button>
          <button
            onClick={() => {
              setShowCreateFolder(false)
              setNewFolderName('')
            }}
            className="cinema-btn-ghost text-xs px-4 py-2 min-h-[36px]"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Contextual save hint inside Projects */}
      {inProjects && (
        <p
          className="font-sans text-xs text-cinema-muted mb-6"
          style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
        >
          Save your creative work here so you can find and continue it from Digital Lab.
        </p>
      )}

      {/* ─── Recent Work — primary view at the workspace root ──────────────── */}
      {/* The student returns to Digital Lab and immediately sees what they were
          working on, without navigating folders. Each row opens the file in its
          approved native application via the existing openFile() flow. */}
      {!currentPath && (
        <section className="mb-14">
          <div className="flex items-center gap-2 mb-5">
            <Clock size={14} className="text-cinema-glow" />
            <span className="cinema-label text-cinema-glow">Recent Work</span>
          </div>

          {recentLoading ? (
            <div className="py-10 text-center">
              <Loader2 size={24} className="mx-auto text-cinema-gold/60 animate-spin" />
            </div>
          ) : recentFiles.length === 0 ? (
            <div className="cinema-empty py-10">
              <Clock size={28} className="mx-auto mb-3 text-cinema-muted/50" />
              <p
                className="font-sans text-sm text-cinema-muted max-w-md mx-auto"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
              >
                No recent work yet. Launch a creative application and save your
                work into your SMA workspace, or import an existing file.
              </p>
            </div>
          ) : (
            <div className="cinema-list">
              {recentFiles.map((entry, idx) => {
                const isOpening = opening === entry.name
                // entry.path is the workspace-relative path (e.g. "My Work/Logos/Logo1.psd").
                // openFile() expects an entry with .name and uses currentPath to build the
                // full path, so we synthesize a pseudo-entry and call openFile with the
                // folder context derived from entry.path.
                const dirPath = entry.path.includes('/')
                  ? entry.path.slice(0, entry.path.lastIndexOf('/'))
                  : ''
                return (
                  <div
                    key={`recent-${idx}`}
                    className="cinema-list-row group px-3 -mx-3 border-l border-transparent hover:border-cinema-gold/40 hover:bg-white/[0.025] transition-colors"
                  >
                    <File size={18} className="text-cinema-muted group-hover:text-cinema-gold transition-colors shrink-0" />
                    <button
                      type="button"
                      onClick={() => openFile({ name: entry.name }, dirPath)}
                      disabled={isOpening}
                      className="flex-1 min-w-0 text-left disabled:cursor-wait"
                    >
                      <span className="block font-sans text-sm text-white font-medium truncate">
                        {entry.name}
                      </span>
                      <span
                        className="block font-sans text-xs text-cinema-muted mt-0.5"
                        style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                      >
                        {fileTypeLabel(entry.name)} · {formatDate(entry.modified)}
                      </span>
                    </button>
                    {isOpening ? (
                      <Loader2 size={16} className="text-cinema-glow animate-spin shrink-0" />
                    ) : (
                      <ChevronRight size={16} className="text-cinema-muted/30 group-hover:text-cinema-gold/60 transition-colors shrink-0" />
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </section>
      )}

      {/* ─── Projects — folder browser (secondary) ──────────────────────────── */}
      {/* Always available for deliberate folder browsing. At the workspace root
          it appears below Recent Work with a section label. Inside a folder it
          is the only view (the existing folder/file listing). */}
      {!currentPath && (
        <div className="flex items-center gap-2 mb-5">
          <FolderOpen size={14} className="text-cinema-gold/60" />
          <span className="cinema-label">Projects</span>
        </div>
      )}

      {/* Workspace + SMA Resources */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-10 xl:gap-16">
        {/* Workspace browser — primary working area */}
        <section className="xl:col-span-8 2xl:col-span-9 min-w-0">
          {/* Storage info — quiet, single line */}
          {disk && (
            <div className="flex items-center gap-2 mb-6 text-cinema-muted">
              <HardDrive size={14} className="text-cinema-gold/60" />
              <span
                className="font-sans text-xs"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
              >
                {formatBytes(disk.available_bytes)} available on this workstation
              </span>
            </div>
          )}

          {/* Loading */}
          {listingLoading && (
            <div className="py-16 text-center">
              <Loader2 size={28} className="mx-auto text-cinema-gold/60 animate-spin" />
            </div>
          )}

          {/* Error */}
          {listingError && !listingLoading && (
            <div className="cinema-empty">
              <AlertCircle size={32} className="mx-auto mb-3 text-cinema-gold/60" />
              <p
                className="font-sans text-sm text-cinema-soft"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
              >
                {listingError.message}
              </p>
            </div>
          )}

          {/* Empty state */}
          {!listingLoading && !listingError && entries.length === 0 && (
            <div className="cinema-empty py-12">
              <FolderOpen size={32} className="mx-auto mb-4 text-cinema-muted/50" />
              <h2
                className="text-base sm:text-lg text-cinema-soft mb-2"
                style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700 }}
              >
                No work here yet.
              </h2>
              <p
                className="font-sans text-sm text-cinema-muted max-w-md mx-auto mb-6"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
              >
                Save your creative files here and they&rsquo;ll appear in Digital Lab.
              </p>
              <button
                onClick={() => importInputRef.current?.click()}
                disabled={importing}
                className="cinema-btn-ghost text-xs sm:text-sm px-5 py-3 min-h-[44px] disabled:opacity-50"
              >
                {importing ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                Import a file
              </button>
            </div>
          )}

          {/* Listing */}
          {!listingLoading && !listingError && entries.length > 0 && (
            <div className="cinema-list">
              {/* Folders */}
              {folders
                .filter((entry) => {
                  // At the root, only show the primary folders. Inside a
                  // folder, show everything (student-created subfolders, etc.).
                  if (!currentPath) return ROOT_VISIBLE_FOLDERS.includes(entry.name)
                  return true
                })
                .map((entry, idx) => {
                const isDefault = !currentPath && DEFAULT_FOLDERS.includes(entry.name)
                const rootDescription = !currentPath ? ROOT_FOLDER_DESCRIPTIONS[entry.name] : null
                return (
                  <button
                    key={`f-${idx}`}
                    type="button"
                    onClick={() => navigateInto(entry.name)}
                    className="cinema-list-row group w-full text-left px-3 -mx-3 border-l border-transparent hover:border-cinema-gold/40 hover:bg-white/[0.025] transition-colors cursor-pointer"
                  >
                    <FolderOpen
                      size={18}
                      className={`shrink-0 ${isDefault ? 'text-cinema-gold' : 'text-cinema-muted group-hover:text-cinema-gold'} transition-colors`}
                    />
                    <div className="flex-1 min-w-0">
                      <span className="block font-sans text-sm text-white font-medium truncate">
                        {entry.name}
                      </span>
                      <span
                        className="block font-sans text-xs text-cinema-muted mt-0.5"
                        style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                      >
                        {rootDescription || `Updated ${formatDate(entry.modified)}`}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          const itemPath = currentPath ? `${currentPath}/${entry.name}` : entry.name
                          if (confirm(`Delete "${entry.name}" and everything inside it?`)) {
                            deleteMutation.mutate(itemPath)
                          }
                        }}
                        className="p-1.5 text-cinema-muted hover:text-red-400 transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                    <ChevronRight size={16} className="text-cinema-muted/30 group-hover:text-cinema-gold/60 transition-colors shrink-0" />
                  </button>
                )
              })}

              {/* Files */}
              {files.map((entry, idx) => {
                const filePath = currentPath ? `${currentPath}/${entry.name}` : entry.name
                const isOpening = opening === entry.name
                return (
                  <div
                    key={`file-${idx}`}
                    className="cinema-list-row group px-3 -mx-3 border-l border-transparent hover:border-cinema-gold/40 hover:bg-white/[0.025] transition-colors"
                  >
                    <File size={18} className="text-cinema-muted group-hover:text-cinema-gold transition-colors shrink-0" />
                    <button
                      type="button"
                      onClick={() => openFile(entry)}
                      disabled={isOpening}
                      className="flex-1 min-w-0 text-left disabled:cursor-wait"
                    >
                      <span className="block font-sans text-sm text-white font-medium truncate">
                        {entry.name}
                      </span>
                      <span
                        className="block font-sans text-xs text-cinema-muted mt-0.5"
                        style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                      >
                        {fileTypeLabel(entry.name)}
                      </span>
                    </button>
                    <span
                      className="font-sans text-xs text-cinema-muted hidden sm:block shrink-0"
                      style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                    >
                      {formatDate(entry.modified)}
                    </span>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                      <button
                        onClick={() => handleDownload(filePath)}
                        className="p-1.5 text-cinema-muted hover:text-cinema-gold transition-colors"
                        title="Download"
                      >
                        <Download size={15} />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Delete "${entry.name}"?`)) {
                            deleteMutation.mutate(filePath)
                          }
                        }}
                        className="p-1.5 text-cinema-muted hover:text-red-400 transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                    {isOpening ? (
                      <Loader2 size={16} className="text-cinema-glow animate-spin shrink-0" />
                    ) : (
                      <ChevronRight size={16} className="text-cinema-muted/30 group-hover:text-cinema-gold/60 transition-colors shrink-0" />
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </section>

        {/* SMA Resources — secondary rail */}
        <aside className="xl:col-span-4 2xl:col-span-3 min-w-0 xl:border-l xl:border-white/5 xl:pl-10 2xl:pl-14">
          <span className="cinema-label block mb-4">SMA Resources</span>
          {resourcesLoading ? (
            <div className="py-8 text-center">
              <Loader2 size={24} className="mx-auto text-cinema-gold/60 animate-spin" />
            </div>
          ) : resources.length === 0 ? (
            <p
              className="font-sans text-sm text-cinema-muted py-2"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              No resources available yet. Your instructors may add resources here.
            </p>
          ) : (
            <div className="cinema-list">
              {resources.map((entry, idx) => (
                <div key={`r-${idx}`} className="cinema-list-row">
                  <File size={16} className="text-cinema-muted shrink-0" />
                  <span
                    className="flex-1 min-w-0 font-sans text-sm text-cinema-soft truncate"
                    style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                  >
                    {entry.name}
                  </span>
                  <span
                    className="font-sans text-xs text-cinema-muted shrink-0"
                    style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                  >
                    {formatBytes(entry.size)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </aside>
      </div>

      {/* Offline note */}
      <div className="flex items-start gap-3 text-xs text-cinema-muted mt-12 pt-8 border-t border-white/5">
        <WifiOff size={14} className="shrink-0 mt-0.5" />
        <p style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Your workspace is stored locally on this workstation. If the internet
          connection is lost, you can continue working with files already on this
          computer. Cloud backup and synchronization will be introduced in a
          future Digital Lab version.
        </p>
      </div>
    </div>
  )
}
